import React, { useEffect, useRef, useState } from 'react';
import './AccessibilityWrapper.css';

/**
 * AccessibilityWrapper component with comprehensive ARIA support
 * Based on ArbiWin design system and European accessibility regulations
 *
 * @param {Object} props - AccessibilityWrapper properties
 * @param {React.ReactNode} props.children - Child components to wrap
 * @param {string} props.role - ARIA role attribute
 * @param {string} props.ariaLabel - ARIA label for screen readers
 * @param {string} props.ariaLabelledBy - ID of element that labels this component
 * @param {string} props.ariaDescribedBy - ID of element that describes this component
 * @param {boolean} props.ariaHidden - Whether to hide from screen readers
 * @param {boolean} props.focusable - Whether the wrapper can receive focus
 * @param {string} props.tabIndex - Tab index for keyboard navigation
 * @param {boolean} props.landmark - Whether this is a landmark region
 * @param {string} props.landmarkLabel - Label for landmark regions
 * @param {boolean} props.live - Whether this is a live region
 * @param {string} props.liveMode - Live region mode: 'polite', 'assertive', 'off'
 * @param {boolean} props.atomic - Whether live region should be read as a whole
 * @param {string} props.className - Additional CSS classes
 * @param {string} props.testId - Test ID for testing
 * @param {string} props.as - HTML element to render as
 * @returns {JSX.Element} AccessibilityWrapper component
 */
const AccessibilityWrapper = function AccessibilityWrapper({
  children,
  role,
  ariaLabel,
  ariaLabelledBy,
  ariaDescribedBy,
  ariaHidden = false,
  focusable = false,
  tabIndex,
  landmark = false,
  landmarkLabel,
  live = false,
  liveMode = 'polite',
  atomic = false,
  className = '',
  testId,
  as = 'div'
}) {

  const wrapperRef = useRef(null);
  const [announcements, setAnnouncements] = useState([]);

  const wrapperClasses = [
    'arbiwin-accessibility-wrapper',
    landmark ? 'arbiwin-accessibility-wrapper--landmark' : '',
    live ? 'arbiwin-accessibility-wrapper--live' : '',
    focusable ? 'arbiwin-accessibility-wrapper--focusable' : '',
    className
  ].filter(Boolean).join(' ');

  // Determine the appropriate ARIA attributes
  const ariaAttributes = {};

  if (role) ariaAttributes.role = role;
  if (ariaLabel) ariaAttributes['aria-label'] = ariaLabel;
  if (ariaLabelledBy) ariaAttributes['aria-labelledby'] = ariaLabelledBy;
  if (ariaDescribedBy) ariaAttributes['aria-describedby'] = ariaDescribedBy;
  if (ariaHidden) ariaAttributes['aria-hidden'] = 'true';

  // Live region attributes
  if (live) {
    ariaAttributes['aria-live'] = liveMode;
    if (atomic) ariaAttributes['aria-atomic'] = 'true';
  }

  // Focus management attributes
  if (focusable || tabIndex !== undefined) {
    ariaAttributes.tabIndex = tabIndex || (focusable ? '0' : '-1');
  }

  // Landmark attributes
  if (landmark && landmarkLabel) {
    ariaAttributes['aria-label'] = landmarkLabel;
  }

  // Handle dynamic announcements for live regions
  const announce = function(message, mode = 'polite') {
    if (!live) return;

    const announcement = {
      id: Date.now(),
      message,
      mode,
      timestamp: new Date()
    };

    setAnnouncements(function(prev) {
      return [...prev, announcement];
    });

    // Clear announcement after it's been read
    setTimeout(function() {
      setAnnouncements(function(prev) {
        return prev.filter(function(item) {
          return item.id !== announcement.id;
        });
      });
    }, 1000);
  };

  // Expose announce function to parent components
  useEffect(function() {
    if (wrapperRef.current && live) {
      wrapperRef.current.announce = announce;
    }
  }, [live]);

  const Element = as;

  return (
    <Element
      ref={wrapperRef}
      className={wrapperClasses}
      data-testid={testId || 'arbiwin-accessibility-wrapper'}
      {...ariaAttributes}
    >
      {children}
      
      {/* Live region announcements */}
      {live && announcements.length > 0 && (
        <div className="arbiwin-accessibility-wrapper__announcements">
          {announcements.map(function(announcement) {
            return (
              <div
                key={announcement.id}
                aria-live={announcement.mode}
                aria-atomic="true"
                className="arbiwin-accessibility-wrapper__announcement"
              >
                {announcement.message}
              </div>
            );
          })}
        </div>
      )}
    </Element>
  );
};

/**
 * LandmarkWrapper component - Pre-configured for landmark regions
 *
 * @param {Object} props - LandmarkWrapper properties
 * @param {React.ReactNode} props.children - Child components
 * @param {string} props.type - Landmark type: 'main', 'navigation', 'banner', 'contentinfo', 'complementary', 'search'
 * @param {string} props.label - Landmark label
 * @param {string} props.className - Additional CSS classes
 * @returns {JSX.Element} LandmarkWrapper component
 */
const LandmarkWrapper = function LandmarkWrapper({
  children,
  type = 'main',
  label,
  className = ''
}) {

  const landmarkRoles = {
    main: 'main',
    navigation: 'navigation',
    banner: 'banner',
    contentinfo: 'contentinfo',
    complementary: 'complementary',
    search: 'search'
  };

  const defaultLabels = {
    main: 'Основно съдържание',
    navigation: 'Навигация',
    banner: 'Заглавие на страницата',
    contentinfo: 'Информация за страницата',
    complementary: 'Допълнителна информация',
    search: 'Търсене'
  };

  return (
    <AccessibilityWrapper
      role={landmarkRoles[type]}
      ariaLabel={label || defaultLabels[type]}
      landmark={true}
      landmarkLabel={label || defaultLabels[type]}
      className={'arbiwin-landmark-wrapper arbiwin-landmark-wrapper--' + type + ' ' + className}
      as={type === 'main' ? 'main' : type === 'navigation' ? 'nav' : 'section'}
    >
      {children}
    </AccessibilityWrapper>
  );
};

/**
 * LiveRegionWrapper component - Pre-configured for live regions
 *
 * @param {Object} props - LiveRegionWrapper properties
 * @param {React.ReactNode} props.children - Child components
 * @param {string} props.mode - Live region mode: 'polite', 'assertive', 'off'
 * @param {boolean} props.atomic - Whether to read as a whole
 * @param {string} props.label - Region label
 * @param {string} props.className - Additional CSS classes
 * @returns {JSX.Element} LiveRegionWrapper component
 */
const LiveRegionWrapper = function LiveRegionWrapper({
  children,
  mode = 'polite',
  atomic = true,
  label = 'Динамично съдържание',
  className = ''
}) {
  return (
    <AccessibilityWrapper
      role="status"
      ariaLabel={label}
      live={true}
      liveMode={mode}
      atomic={atomic}
      className={'arbiwin-live-region-wrapper ' + className}
    >
      {children}
    </AccessibilityWrapper>
  );
};

/**
 * FormWrapper component - Pre-configured for form accessibility
 *
 * @param {Object} props - FormWrapper properties
 * @param {React.ReactNode} props.children - Child components
 * @param {string} props.label - Form label
 * @param {string} props.description - Form description
 * @param {boolean} props.required - Whether form has required fields
 * @param {Array} props.errors - Form validation errors
 * @param {string} props.className - Additional CSS classes
 * @returns {JSX.Element} FormWrapper component
 */
const FormWrapper = function FormWrapper({
  children,
  label,
  description,
  required = false,
  errors = [],
  className = ''
}) {

  const formId = 'form-' + Date.now();
  const descriptionId = description ? formId + '-description' : undefined;
  const errorsId = errors.length > 0 ? formId + '-errors' : undefined;

  const ariaDescribedBy = [descriptionId, errorsId].filter(Boolean).join(' ');

  return (
    <AccessibilityWrapper
      role="form"
      ariaLabel={label}
      ariaDescribedBy={ariaDescribedBy || undefined}
      className={'arbiwin-form-wrapper ' + className}
      as="form"
    >
      {label && (
        <h2 className="arbiwin-form-wrapper__title">
          {label}
          {required && (
            <span className="arbiwin-form-wrapper__required" aria-label="Задължителни полета">
              *
            </span>
          )}
        </h2>
      )}

      {description && (
        <p id={descriptionId} className="arbiwin-form-wrapper__description">
          {description}
        </p>
      )}

      {errors.length > 0 && (
        <div
          id={errorsId}
          role="alert"
          aria-live="polite"
          className="arbiwin-form-wrapper__errors"
        >
          <h3 className="arbiwin-form-wrapper__errors-title">
            Грешки при валидация:
          </h3>
          <ul className="arbiwin-form-wrapper__errors-list">
            {errors.map(function(error, index) {
              return (
                <li key={index} className="arbiwin-form-wrapper__error">
                  {error}
                </li>
              );
            })}
          </ul>
        </div>
      )}

      <div className="arbiwin-form-wrapper__content">
        {children}
      </div>
    </AccessibilityWrapper>
  );
};

/**
 * DialogWrapper component - Pre-configured for dialog accessibility
 *
 * @param {Object} props - DialogWrapper properties
 * @param {React.ReactNode} props.children - Child components
 * @param {string} props.title - Dialog title
 * @param {string} props.description - Dialog description
 * @param {boolean} props.modal - Whether dialog is modal
 * @param {Function} props.onClose - Close handler
 * @param {string} props.className - Additional CSS classes
 * @returns {JSX.Element} DialogWrapper component
 */
const DialogWrapper = function DialogWrapper({
  children,
  title,
  description,
  modal = true,
  onClose,
  className = ''
}) {

  const dialogId = 'dialog-' + Date.now();
  const titleId = title ? dialogId + '-title' : undefined;
  const descriptionId = description ? dialogId + '-description' : undefined;

  const ariaLabelledBy = titleId;
  const ariaDescribedBy = descriptionId;

  return (
    <AccessibilityWrapper
      role={modal ? 'dialog' : 'alertdialog'}
      ariaModal={modal ? 'true' : undefined}
      ariaLabelledBy={ariaLabelledBy}
      ariaDescribedBy={ariaDescribedBy}
      focusable={true}
      className={'arbiwin-dialog-wrapper ' + className}
    >
      {title && (
        <h2 id={titleId} className="arbiwin-dialog-wrapper__title">
          {title}
        </h2>
      )}

      {description && (
        <p id={descriptionId} className="arbiwin-dialog-wrapper__description">
          {description}
        </p>
      )}

      <div className="arbiwin-dialog-wrapper__content">
        {children}
      </div>

      {onClose && (
        <button
          type="button"
          className="arbiwin-dialog-wrapper__close"
          onClick={onClose}
          aria-label="Затвори диалога"
        >
          <span aria-hidden="true">&times;</span>
        </button>
      )}
    </AccessibilityWrapper>
  );
};

AccessibilityWrapper.displayName = 'AccessibilityWrapper';
LandmarkWrapper.displayName = 'LandmarkWrapper';
LiveRegionWrapper.displayName = 'LiveRegionWrapper';
FormWrapper.displayName = 'FormWrapper';
DialogWrapper.displayName = 'DialogWrapper';

export default AccessibilityWrapper;
export { LandmarkWrapper, LiveRegionWrapper, FormWrapper, DialogWrapper };
